/**
 * Detection helpers for English exam paper question sub-types.
 *
 * Uses the explicit `question_subtype` field set by the AI (most reliable),
 * with heuristic fallbacks for older imports that pre-date the field.
 *
 * Sub-types:
 *   "error_finding_standalone" — standalone sentence spelling/punctuation MCQ (Rule 33)
 *   "error_finding_context"    — continuous passage error-finding sub-question (Rule 34)
 *   "cloze_word_choice"        — cloze word-choice passage sub-question (Rule 35)
 */

/**
 * Returns true when a standalone sentence MCQ question should render
 * with the bracket-marker style (sentence + A/B/C/D/N buttons).
 *
 * Matches:
 *  - explicit question_subtype = "error_finding_standalone"
 *  - question_text contains ↑↑ boundary markers (new AI format)
 *  - question_text contains single ↑ markers (older DataLab format)
 *  - options include an "N" (no mistake) choice — definitive signal
 *  - section_title or section_instructions mentions spelling/punctuation mistakes
 */
export const isErrorFindingStandalone = (question) => {
  if (!question) return false;

  if (question.question_subtype === 'error_finding_standalone') return true;
  if (question.question_subtype && question.question_subtype !== 'error_finding_standalone') return false;

  // Heuristic: must be multiple-choice
  if (question.type !== 'multiple-choice') return false;

  const text = question.question_text || '';
  const opts = question.options || [];
  const sectionTitle = question.section_title || '';
  const sectionInstructions = question.section_instructions || '';

  const hasUpMarkers = /↑/.test(text);
  const hasNoMistakeOption = opts.some((o) =>
    /^N$/i.test(((o.option_text || o.text || '')).trim())
  );
  const sectionBlob = `${sectionTitle} ${sectionInstructions}`;
  const isErrorFindingSection =
    /(?:spelling|punctuation)\s+mistake/i.test(sectionBlob) ||
    /find\s+the\s+(?:spelling|punctuation)\s+mistake/i.test(sectionBlob) ||
    /circle\s+the\s+(?:spelling|punctuation)\s+mistake/i.test(sectionBlob);

  return hasUpMarkers || hasNoMistakeOption || isErrorFindingSection;
};

/**
 * Returns true when a question is a continuous-passage error-finding sub-question (Rule 34).
 * These are individual MCQ sub-questions inside a grouped context passage.
 */
export const isErrorFindingContext = (question) => {
  if (!question) return false;
  if (question.question_subtype === 'error_finding_context') return true;
  if (question.question_subtype && question.question_subtype !== 'error_finding_context') return false;

  // Heuristic: MCQ with N option inside a grouped context passage
  if (question.type !== 'multiple-choice') return false;
  const opts = question.options || [];
  const hasNoMistakeOption = opts.some((o) =>
    /^N$/i.test(((o.option_text || o.text || '')).trim())
  );
  return hasNoMistakeOption;
};

/**
 * Returns true when a question is a cloze word-choice sub-question (Rule 35).
 * These are individual MCQ sub-questions inside a grouped cloze passage.
 */
export const isClozeWordChoice = (question) => {
  if (!question) return false;
  if (question.question_subtype === 'cloze_word_choice') return true;
  if (question.question_subtype && question.question_subtype !== 'cloze_word_choice') return false;

  // Heuristic: MCQ sub-question inside a grouped context passage with [___] marker
  if (question.type !== 'multiple-choice') return false;
  const text = question.question_text || '';
  const sectionInstructions = question.section_instructions || '';
  return (
    /\[___\]|\[_+\]/.test(text) ||
    /choose the best word|best word.*group of words|group of words.*complete/i.test(sectionInstructions)
  );
};

/**
 * Returns true when a grouped question's passage is a cloze word-choice exercise (Rule 35).
 * Used at the group level to detect "Caught Out?" / "Performance Time" style passages.
 */
export const isGroupedClozePassage = (question) => {
  if (!question || question.type !== 'grouped') return false;
  if (question.question_subtype === 'cloze_word_choice') return true;
  if (question.question_subtype === 'error_finding_context') return false;

  // Heuristic: context passage where sub-questions are cloze word-choice
  if (question.passage_type !== 'context') return false;
  const sectionInstructions = question.section_instructions || '';
  const subQuestions = question.sub_questions || [];

  if (/choose the best word|best word.*group of words|group of words.*complete/i.test(sectionInstructions)) {
    return true;
  }
  // If any sub-question has a [___] marker in question_text
  return subQuestions.some((sq) => /\[___\]|\[_+\]/.test(sq.question_text || ''));
};

/**
 * Returns true when a grouped question's passage is a continuous error-finding exercise (Rule 34).
 * Used at the group level to detect "A Ghostly Encounter" / "Hippos" style passages.
 */
export const isGroupedErrorFindingPassage = (question) => {
  if (!question || question.type !== 'grouped') return false;
  if (question.question_subtype === 'error_finding_context') return true;
  if (question.question_subtype) return false;

  // Heuristic: context passage with N (no mistake) options in sub-questions
  if (question.passage_type !== 'context') return false;
  const subQuestions = question.sub_questions || [];
  return subQuestions.some((sq) =>
    (sq.options || []).some((o) =>
      /^N$/i.test(((o.option_text || o.text || '')).trim())
    )
  );
};

/**
 * Parse word-group segments from question_text that contains ↑↑ boundary markers.
 * Falls back to using the options array directly if no markers found.
 *
 * Returns array of segment strings (without the markers themselves).
 * The last option "N" is NOT included — it is handled separately as "No mistake".
 */
export const parseErrorFindingSegments = (questionText, options) => {
  if (!questionText) return [];

  // Try ↑↑ delimiter format (new AI format): "Group A ↑↑ Group B ↑↑ Group C ↑↑ Group D"
  if (/↑↑/.test(questionText)) {
    const segments = questionText
      .split(/\s*↑↑\s*/)
      .map((s) => s.replace(/↑/g, '').trim())
      .filter(Boolean);
    // If we got segments and they match the option count (excluding N), return them
    const nonNOptions = (options || []).filter(
      (o) => !/^N$/i.test(((o.option_text || o.text || '')).trim())
    );
    if (segments.length > 0 && segments.length <= nonNOptions.length + 1) {
      return segments;
    }
  }

  // Fall back to options text (most reliable)
  return (options || [])
    .filter((o) => !/^N$/i.test(((o.option_text || o.text || '')).trim()))
    .map((o) => o.option_text || o.text || '');
};

/**
 * Get the clean sentence text for display.
 * Strips:
 *   - ↑↑ boundary markers (new AI format)
 *   - single ↑ markers (DataLab format)
 *   - trailing letter sequences like " A B C D" or " A B C D E" that DataLab
 *     appends to represent the bracket marker row in plain text
 */
export const getCleanSentenceText = (questionText) => {
  if (!questionText) return '';
  return questionText
    .replace(/\s*↑↑\s*/g, ' ')
    .replace(/↑/g, '')
    // Strip DataLab-appended letter labels at end: " A B C D" / " A B C D E"
    .replace(/\s+[A-E](\s+[A-E]){1,4}\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
};
